import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Building2, HeartPulse, HardHat, Wrench, ShieldCheck,
  ClipboardCheck, FolderCheck, Layers, GitBranch, Gauge,
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";
import { EnterpriseEnquiry } from "@/components/marketing/PricingTable";

const NAVY = "#071426";
const BLUE = "#2585FF";
const TEAL = "#19C5C5";
const GREY = "#F3F6FA";

const DEMO_URL = "https://demo.videra.app";
const KS = "https://keystoneconsulting.uk";

const HERO_IMAGE = `${KS}/images/nhs/nhs-pmo-command-centre.jpg`;
const VINCI_LOGO = `${KS}/images/clients/vinci-facilities.png`;

const STATS = [
  ["£1.4bn", "Portfolio value under management"],
  ["115", "Active schemes & projects"],
  ["1,738", "Days of lifecycle horizon tracked"],
  ["24/7", "Evidence and audit rails"],
];

const CHAIN = [
  ["Lifecycle", "What needs to happen next across the estate."],
  ["Projects", "Schemes scoped, programmed and delivered."],
  ["Approvals", "Governance gates with a defensible trail."],
  ["Delivery", "Progress, cost and risk in one live view."],
  ["Evidence", "Proof attached to the work, always current."],
  ["Handback", "Progressive readiness, years before expiry."],
];

const DEMOS = [
  ["fm", "FM & Hard Services", Wrench,
    "The environment VINCI Facilities uses in production. Assets, lifecycle, operational requirements, projects and evidence on one set of rails.",
    `${KS}/images/healthcare/hc-handback-readiness.jpg`,
    ["Lifecycle and asset registers", "PFI obligation tracking", "Progressive handback readiness", "Evidence and audit rails"]],
  ["construction", "Construction", HardHat,
    "Tier-1 scheme delivery from initiation and governance through to handover and future lifecycle, with commercial control throughout.",
    `${KS}/images/construction/cp-live-portfolio.jpg`,
    ["Work stage and phase gates", "Contract and commercial control", "Site progress and quality", "Handover information"]],
  ["nhs", "NHS & Healthcare", HeartPulse,
    "Capital projects, lifecycle programmes, approvals and board reporting designed around the way healthcare estates actually run.",
    `${KS}/images/nhs/nhs-portfolio-tracker.jpg`,
    ["Capital and business-case gates", "IPC and compliance dashboards", "Governance reporting", "Board packs from live data"]],
];

const BESPOKE = [
  [Layers, "Lifecycle on the same rails as delivery",
    "Standard PPM stops at the project. Bespoke Videra connects the long-range lifecycle plan to the schemes that deliver it, so next year's obligation is visible today."],
  [FolderCheck, "Progressive handback readiness",
    "Readiness scored continuously against contractual requirements rather than assembled in a panic at expiry."],
  [ShieldCheck, "Assurance and evidence rails",
    "Every requirement has an owner, a due date and attached proof — produced as a pack on demand, not reconstructed from folders."],
  [ClipboardCheck, "Governance configured to your model",
    "Your gates, your delegated authorities, your approval routes and your reporting cycle, not a vendor's idea of them."],
  [GitBranch, "Integrations into your estate",
    "CAFM, asset information, finance and document systems joined up so teams stop re-keying between them."],
  [Gauge, "Bespoke dashboards and board packs",
    "Command-centre views composed from live data, built around the questions your board actually asks."],
];

const ENTERPRISE_PM = [
  "Single-tenant deployment with your choice of data residency",
  "SSO and directory integration",
  "Custom frameworks, sector templates and branding",
  "Tenant-bound AI with no cross-customer training",
  "Named onboarding, assurance pack and DPIA support",
  "Volume seat pricing with annual invoicing",
];

const Section = ({ id, dark, tint, children }) => (
  <section id={id} className="px-6 py-20 lg:px-10 lg:py-28"
    style={{ backgroundColor: dark ? NAVY : tint ? GREY : "#FFFFFF" }}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children, dark }) => (
  <p className="text-[11px] font-bold uppercase tracking-[0.18em]"
    style={{ color: dark ? TEAL : BLUE }}>{children}</p>
);

const H2 = ({ children, dark }) => (
  <h2 className="mt-3 font-marketing-display text-3xl font-black leading-[1.08] tracking-tight md:text-4xl lg:text-[2.7rem]"
    style={{ color: dark ? "#FFFFFF" : NAVY }}>{children}</h2>
);

const Primary = ({ to, href, children, testId }) => {
  const cls = "inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5";
  const style = { backgroundColor: BLUE, boxShadow: "0 10px 30px -12px rgba(37,133,255,0.8)" };
  return href
    ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
        data-testid={testId} className={cls} style={style}>{children} <ArrowRight size={15} /></a>
    : <Link to={to} data-testid={testId} className={cls} style={style}>{children} <ArrowRight size={15} /></Link>;
};

const Ghost = ({ to, href, children, testId, dark }) => {
  const cls = `inline-flex items-center justify-center gap-2 rounded-md border-2 px-6 py-3.5 text-sm font-bold transition-colors ${
    dark ? "border-white/25 text-white hover:border-white/60" : "border-slate-300 text-slate-900 hover:border-slate-900"}`;
  return href
    ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
        data-testid={testId} className={cls}>{children} <ArrowRight size={15} /></a>
    : <Link to={to} data-testid={testId} className={cls}>{children} <ArrowRight size={15} /></Link>;
};

export default function Enterprise() {
  return (
    <div className="marketing-root" data-testid="enterprise-page">
      <MarketingNav active="Enterprise Solutions" />

      {/* 1 — Hero */}
      <header className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        <img src={HERO_IMAGE} alt="" aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div aria-hidden="true" className="absolute inset-0"
          style={{ background: `linear-gradient(100deg, ${NAVY} 8%, rgba(7,20,38,0.93) 48%, rgba(7,20,38,0.6) 100%)` }} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Eyebrow dark>Enterprise Videra</Eyebrow>
          <h1 className="mt-4 max-w-4xl font-marketing-display text-4xl font-black leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Enterprise Videra: built around your operating model
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 lg:text-lg">
            Videra PM is the ready-to-use platform. Enterprise Videra is what happens when the
            technology is configured around your governance, lifecycle obligations, assets and
            contracts — the same foundations a Tier-1 FM contractor runs live on NHS hospital
            estates today.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Primary href={DEMO_URL} testId="hero-demo-trial">Start a 14-Day Demo Trial</Primary>
            <Ghost to="/contact" testId="hero-contact" dark>Talk to Us About Enterprise</Ghost>
          </div>
          <div className="mt-14 grid gap-px overflow-hidden rounded-lg border border-white/15 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {STATS.map(([v, l]) => (
              <div key={l} className="px-6 py-7" style={{ backgroundColor: "rgba(7,20,38,0.75)" }}
                data-testid={`stat-${l.split(" ")[0].toLowerCase()}`}>
                <p className="font-marketing-display text-3xl font-black text-white">{v}</p>
                <p className="mt-2 text-xs leading-relaxed text-white/60">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* 2 — VINCI case study */}
      <Section id="vinci" tint>
        <div className="grid items-start gap-14 lg:grid-cols-[1fr_0.85fr]">
          <div>
            <Eyebrow>Live in production</Eyebrow>
            <H2>A Tier-1 FM contractor runs live PFI Hard Services on Videra.</H2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              VINCI Facilities uses Videra technology to connect lifecycle requirements, projects,
              approvals, delivery, evidence and progressive handback readiness across NHS hospital
              estates. They are a Videra client today, live across two large hospital sites in
              England — one in the north, one in the south.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Before Videra, the same picture existed in spreadsheets, shared folders and inboxes:
              lifecycle separated from delivery, evidence scattered, and management reports
              reconstructed by hand every month. Videra put the whole chain on one set of rails.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Primary href={`${KS}/clients`} testId="vinci-story">Read the VINCI Story</Primary>
              <Ghost to="/contact" testId="vinci-case-study">Get the Case Study</Ghost>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <img src={VINCI_LOGO} alt="VINCI Facilities" className="h-12 w-auto object-contain"
              data-testid="vinci-logo" />
            <p className="mt-6 text-sm font-bold uppercase tracking-[0.14em]" style={{ color: BLUE }}>
              The chain, connected
            </p>
            <ol className="mt-5 space-y-4">
              {CHAIN.map(([t, b], i) => (
                <li key={t} className="flex gap-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-black text-white"
                    style={{ backgroundColor: NAVY }}>{i + 1}</span>
                  <div>
                    <p className="text-sm font-bold" style={{ color: NAVY }}>{t}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{b}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* 3 — Demo environments */}
      <Section id="demos">
        <div className="max-w-3xl">
          <Eyebrow>Demo environments</Eyebrow>
          <H2>Look inside three bespoke environments for 14 days.</H2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Rather than another brochure, we give you the keys. A 14-day fully unlocked trial at{" "}
            <span className="font-semibold" style={{ color: NAVY }}>demo.videra.app</span> opens three
            specialist environments, each populated with realistic data and bespoke design.
          </p>
        </div>
        <div className="mt-14 grid gap-7 lg:grid-cols-3">
          {DEMOS.map(([key, title, Icon, body, shot, points]) => (
            <article key={key} data-testid={`demo-card-${key}`}
              className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl">
              <img src={shot} alt={`${title} demo environment`} className="h-44 w-full object-cover" />
              <div className="flex flex-1 flex-col p-7">
                <Icon size={22} strokeWidth={1.75} style={{ color: BLUE }} />
                <h3 className="mt-4 font-marketing-display text-xl font-bold" style={{ color: NAVY }}>{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{body}</p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {points.map((p) => (
                    <li key={p} className="flex gap-2.5 text-sm text-slate-700">
                      <Check size={15} strokeWidth={2.5} className="mt-0.5 shrink-0" style={{ color: TEAL }} />
                      {p}
                    </li>
                  ))}
                </ul>
                <a href={DEMO_URL} target="_blank" rel="noreferrer" data-testid={`demo-open-${key}`}
                  className="mt-7 inline-flex items-center gap-2 text-sm font-bold" style={{ color: BLUE }}>
                  Open the {title} demo <ArrowRight size={14} />
                </a>
              </div>
            </article>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-start gap-4 rounded-lg border p-8 sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: "#DCE4EF", backgroundColor: GREY }}>
          <p className="max-w-2xl text-sm leading-relaxed text-slate-600">
            Prefer us to set the trial up for you, with your own sector and a walkthrough? Request
            access below and we will provision it within one working day.
          </p>
          <div className="flex flex-wrap gap-3">
            <Primary href={DEMO_URL} testId="demos-open-trial">Start the Trial Now</Primary>
            <Ghost href="#request-access" testId="demos-request-access">Request Access</Ghost>
          </div>
        </div>
      </Section>

      {/* 4 — What bespoke adds */}
      <Section id="bespoke" dark>
        <div className="max-w-3xl">
          <Eyebrow dark>Beyond standard PPM</Eyebrow>
          <H2 dark>What bespoke Videra adds to Videra PM.</H2>
          <p className="mt-5 text-base leading-relaxed text-white/70">
            Videra PM manages projects extremely well. Enterprise Videra manages the operating
            model the projects belong to — lifecycle, obligations, assurance and evidence, all
            connected.
          </p>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-lg border border-white/15 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {BESPOKE.map(([Icon, t, b]) => (
            <div key={t} className="p-7" style={{ backgroundColor: NAVY }} data-testid="bespoke-item">
              <Icon size={20} strokeWidth={1.75} style={{ color: TEAL }} />
              <p className="mt-4 text-sm font-bold text-white">{t}</p>
              <p className="mt-2 text-xs leading-relaxed text-white/60">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 5 — Enterprise edition of pm.videra.app */}
      <Section id="enterprise-pm" tint>
        <div className="grid items-center gap-14 lg:grid-cols-[1fr_0.9fr]">
          <div>
            <Eyebrow>Enterprise edition</Eyebrow>
            <H2>Already know Videra PM is the right platform?</H2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              You do not need a bespoke build to get enterprise terms. The same Videra PM you can
              trial today is available as a single-tenant enterprise edition with your residency,
              identity and assurance requirements met. There is no self-serve price for it — just
              tell us what your IT function needs and we will come back with a quote and an
              assurance pack.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Primary to="/contact" testId="enterprise-pm-contact">Contact Us for Enterprise PM</Primary>
              <Ghost to="/pricing" testId="enterprise-pm-pricing">Compare Standard Plans</Ghost>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <Building2 size={22} strokeWidth={1.75} style={{ color: BLUE }} />
            <p className="mt-4 font-marketing-display text-xl font-bold" style={{ color: NAVY }}>
              Included with Enterprise
            </p>
            <ul className="mt-6 space-y-3.5">
              {ENTERPRISE_PM.map((p) => (
                <li key={p} className="flex gap-3 text-sm text-slate-700">
                  <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0" style={{ color: TEAL }} />
                  {p}
                </li>
              ))}
            </ul>
            <p className="mt-7 text-xs text-slate-500">Pricing on application. Annual terms only.</p>
          </div>
        </div>
      </Section>

      {/* 6 — Request access form */}
      <Section id="request-access">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <Eyebrow>Request access</Eyebrow>
            <H2>Tell us where you want to start.</H2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              A demo trial, an enterprise quotation for Videra PM, or a conversation about a bespoke
              environment built around your organisation. One form, one working day, a real person.
            </p>
            <div className="mt-8 space-y-3 text-sm text-slate-600">
              {["14-day fully unlocked demo access",
                "Enterprise quotation and assurance pack",
                "Bespoke scoping conversation with Keystone"].map((x) => (
                <p key={x} className="flex gap-2.5">
                  <Check size={15} strokeWidth={2.5} className="mt-0.5 shrink-0" style={{ color: TEAL }} />{x}
                </p>
              ))}
            </div>
          </div>
          <EnterpriseEnquiry
            heading="Request demo access or an enterprise quote"
            blurb="Tell us about your organisation, the sector you operate in and what you need to see. We will provision demo access or come back with a quotation and assurance pack."
          />
        </div>
      </Section>

      {/* 7 — Closing CTA */}
      <Section dark>
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <H2 dark>See what the platform can do in your world.</H2>
            <p className="mt-4 max-w-2xl text-base text-white/70">
              Start the 14-day demo trial and look inside the FM, Construction and NHS environments,
              or talk to us about an enterprise edition built around your organisation.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Primary href={DEMO_URL} testId="closing-demo-trial">Start a 14-Day Demo Trial</Primary>
            <Ghost to="/contact" testId="closing-contact" dark>Contact Us</Ghost>
          </div>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
